import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  COMPANY_NAME,
  SECTOR,
  SEER_COMPETITOR,
  SIZE_MAX,
  SIZE_MIN,
  STOCK_TICKER,
  WEBSITE,
} from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { OPTIONS_PEER_COMPETITOR } from 'constants/materialAssessment';
import useCompanyCompetitor from 'hooks/useCompanyCompetitor';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCompetitorMACreateMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import getAverageStatEmployees from 'utils/getAvarageMembers';

import useCreateCompetitorForm from './hooks/useCreateCompetitorForm';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IStakeholderCompetitorItem from 'store/slices/materialAssessment/interfaces/IStakeholderCompetitorItem';
import type IFieldError from 'store/types/IFieldError';
import type { TCompetitorCreateSchema } from 'utils/validators';
import type ICreateCompetitor from './interfaces/ICreateCompetitor';

import styles from './scss/CreateCompetitor.module.scss';

function CreateCompetitor({
  open, onClose, title, recordId, setOpenModal, vcmId,
}: ICreateCompetitor) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createCompetitor] = useCompetitorMACreateMutation();
  const { setFieldsErrors } = useFieldsErrors();

  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    errors,
    setError,
    fieldsName,
  } = useCreateCompetitorForm({ isPageLoading: isLoading });

  const {
    onFindCompaniesCompetitor,
    onChangeCompany,
    companyDetails,
    loadingDetails,
    loadingCompanies,
    resetCompanyDetails,
  } = useCompanyCompetitor<TCompetitorCreateSchema>({ watch, setValue });

  const [companySearch, setCompanySearch] = useState<string>('');
  const [companyOptions, setCompanyOptions] = useState<IOption[]>([]);

  const onCancel = () => {
    onClose();
    reset();
    setCompanySearch('');
  };

  const onAddCompetitor = (competitor: IStakeholderCompetitorItem) => {
    createCompetitor({ maId: recordId, data: competitor, vcmId }).then(() => {
      toast.success(i18n.t('notifications.createdCompetitor'));
      setOpenModal(false);
    }).catch((e) => {
      setFieldsErrors<TCompetitorCreateSchema>({ errors: e, setError, fieldsName });
    });
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    onAddCompetitor({
      companyName: data[COMPANY_NAME]?.label || '',
      stockTickers: data[STOCK_TICKER]?.map((item) => item.value) || [],
      website: data[WEBSITE] || '',
      sectors: data[SECTOR]?.map((item) => item.value) || [],
      type: data[SEER_COMPETITOR]?.value || '',
      employeeCount: Number(data[SIZE_MAX]) || 0,
    });

    onCancel();
  });

  const stockTickerOptions = () => companyDetails?.stockTickers?.map((item) => ({
    label: item, value: item,
  }));

  const sectors = () => companyDetails?.sectors?.map((item) => ({
    label: item, value: item,
  }));

  useEffect(() => {
    if (companyDetails) {
      setValue(SIZE_MIN, `${getAverageStatEmployees(companyDetails?.employeeCount || 0)}`);
    }
  }, [companyDetails, setValue]);

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
      showLoader={loadingDetails}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={COMPANY_NAME}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => {
              const selectValue = value?.value ? value : null;

              return (
                <Select
                  label={t('materialityAssessment.fields.companyName')}
                  placeholder={t('materialityAssessment.placeholder.searchCompany')}
                  value={selectValue}
                  inputValue={companySearch}
                  onInputChange={(inputValue, { action }) => {
                    if (action === 'input-change') {
                      setCompanySearch(inputValue);
                      onFindCompaniesCompetitor(inputValue).then((options) => {
                        setCompanyOptions(options);
                      });
                    }

                    return inputValue;
                  }}
                  options={companyOptions}
                  onChange={(newValue) => {
                    resetCompanyDetails();
                    onChange(newValue);
                    if (newValue) {
                      onChangeCompany();
                      setCompanySearch('');
                    }
                  }}
                  error={error?.message}
                  isSearchable
                  isClearable
                  menuPosition="absolute"
                  isLoading={loadingCompanies}
                  required
                />
              );
            }}
          />
          <Controller
            control={control}
            name={STOCK_TICKER}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.stockTicker')}
                placeholder={t('materialityAssessment.placeholder.selectTicker')}
                options={stockTickerOptions()}
                value={value}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                isDisabled={!companyDetails}
                required
                isMulti
              />
            )}
          />

          <Controller
            control={control}
            name={WEBSITE}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.website')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                disabled={!companyDetails}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={SECTOR}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.sector')}
                placeholder={t('materialityAssessment.placeholder.selectSector')}
                options={sectors()}
                value={value}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                isDisabled={!companyDetails}
                required
                isMulti
              />
            )}
          />
          <Controller
            control={control}
            name={SEER_COMPETITOR}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.peerCompetitor')}
                placeholder={t('materialityAssessment.placeholder.selectCategory')}
                options={OPTIONS_PEER_COMPETITOR}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                isDisabled={!companyDetails}
                required
              />
            )}
          />
          <div className={styles.wrapperNumberFields}>
            <span className={styles.label}>
              {`${t('materialityAssessment.fields.size')} *`}
            </span>
            <div className={styles.sizeFields}>
              <Controller
                control={control}
                name={SIZE_MIN}
                render={({
                  field: {
                    name,
                    value,
                    onChange,
                  },
                }) => (
                  <NumberField
                    name={name}
                    value={value}
                    onChange={(values) => onChange(values.value)}
                    placeholder={t('materialityAssessment.placeholder.min')}
                    allowNegative={false}
                    disabled={!companyDetails}
                  />
                )}
              />
              <span>-</span>
              <Controller
                control={control}
                name={SIZE_MAX}
                render={({
                  field: {
                    name,
                    value,
                    onChange,
                  },
                }) => (
                  <NumberField
                    name={name}
                    value={value}
                    onChange={(values) => onChange(values.value)}
                    placeholder={t('materialityAssessment.placeholder.max')}
                    allowNegative={false}
                    disabled={!companyDetails}
                  />
                )}
              />
            </div>
            <span className={styles.errorMessage}>
              {errors[SIZE_MIN]?.message || errors[SIZE_MAX]?.message}
            </span>
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateCompetitor;
